import {useContext} from 'react';

import {useFloatingParentNodeId} from '@floating-ui/react';

import {useDropdown} from '@pb/kit-component-dropdown-v2';
import {useControlledState} from '@pb/kit-hooks-use-controlled-state';
import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';

import {DropdownMenuContext} from './dropdown-menu-context';

import type {UseDropdownProps, UseDropdownReturnType} from '@pb/kit-component-dropdown-v2';
import type {ComponentProps} from 'react';


export interface UseDropdownMenuProps extends UseDropdownProps {
    /**
     * If all menu levels should be hidden on item click
     * */
    hideOnItemClick?: boolean,
    /**
     * If the dropdown menu should be nested
     * */
    nested?: boolean,
}

/**
 * $FlowIgnoreMe WHY: Flow cannot resolve inexact type merge
 **/
export type UseDropdownMenuReturn = Omit<UseDropdownReturnType, 'getDropdownProps' | 'activeIndex' | 'getItemProps'> & {
    getDropdownMenuProps: (props?: ComponentProps<'div'>) => ReturnType<UseDropdownReturnType['getDropdownProps']> & {
        type: 'ABSOLUTE' | 'FIXED',
        maxHeight: number,
        maxWidth: number,
        onItemSelect?: () => void,
        activeIndex: UseDropdownReturnType['activeIndex'],
        getItemProps: UseDropdownReturnType['getItemProps'],
    },
};

export const useDropdownMenu = ({
    shown: externalShown,
    setShown: externalSetShown,
    hideOnItemClick = true,
    role = 'menu',
    // average height of 10 items + padding
    maxHeight = 330,
    maxWidth = 400,
    type = 'ABSOLUTE',
    nested: nestedProp,
    ...props
}: UseDropdownMenuProps): UseDropdownMenuReturn => {

    const [shown, setShown] = useControlledState<boolean>(
        externalShown,
        externalSetShown,
        false,
    );

    const hasParent = useFloatingParentNodeId() !== null;
    const nested = nestedProp ?? hasParent;
    const DropdownMenuContextParentValue = useContext(DropdownMenuContext);

    const onItemSelect = useCallbackWithRef(() => {
        if (DropdownMenuContextParentValue.onItemSelect) {
            DropdownMenuContextParentValue.onItemSelect();
            return;
        }
        if (hideOnItemClick) {
            // For some reason
            // in case of synchronous closing
            // useListNavigation with FloatingFocusManager dispatch click event on the anchor element,
            // which triggers open again
            setTimeout(() => {
                setShown(false);
            }, 0);
        }
    });

    const {
        getDropdownProps,
        activeIndex,
        getItemProps,
        ...rest
    } = useDropdown({
        verticalOffset: nested ? 1 : 0,
        arrowEnabled: !nested,
        ...props,
        type,
        shown,
        setShown,
        role,
        maxHeight,
        maxWidth,
        listNavigationEnabled: true,
    });

    return {
        ...rest,
        getDropdownMenuProps: _props => ({
            ...getDropdownProps(_props),
            maxHeight,
            maxWidth,
            onItemSelect,
            activeIndex,
            getItemProps,
            type,
        }),
    };
};
